import { GOVERNED_RECORDS, type GovernedRecord } from './governed-records';
import { createJsonStore, isCurrentQuarantineText, type JsonStoreOptions } from './json-store';
import type { KeyValueStore } from './key-value-store';
import { QUARANTINE_PREFIX } from './keys';

/**
 * The launch-time decontamination pass (review round 6, SC-4).
 *
 * It replaces the round-5 launch sweep, which had the right idea and the wrong position. That
 * sweep ran after migrations and validated through the session view, which left one architectural
 * duality behind five separate leaks: it ran late enough for memory overlays and per-key callback
 * flushes to exist, and it asked the overlay layer what was stored instead of asking the backend.
 * Round 6 removes the duality rather than patching the symptoms.
 *
 * Three properties, and every finding it closes falls out of one of them:
 *
 *   1. **One scope.** The whole pass runs inside a single governed scope holding *both* deferral
 *      counters, and — at the construction seam — inside the key/value store's own construction
 *      scope as well. No application callback runs between the first physical read and the last
 *      physical erase. A throwing warning handler cannot abort the pass before it starts (R6-1),
 *      and a hostile one cannot disable the backend between two keys and strand the second
 *      (R6-2).
 *   2. **Physical bytes.** Every decision reads `readPhysical`/`physicalKeys`, never the session
 *      view. A clean value written into a degraded session's overlay no longer hides the
 *      contaminated bytes underneath it (R6-3). This is structural, not a consequence of running
 *      early: the same code called later in the session still reads the backend. A read that
 *      *fails* is a third answer, not absence: it is erased fail-closed (R7-1).
 *   3. **Fail-closed namespace ownership.** Nothing under `thrive.v1.` is left unexamined.
 *      Governed keys — `meta` included, with its own projector (R6-5) — are projected and
 *      sanitised or erased; quarantine diagnostics must match this build's exact handwriting
 *      down to the key they are filed against and the shape of their timestamp (R6-4); anything
 *      else has no legitimate writer and is erased. Keys outside the namespace are not ours and
 *      are never touched.
 *
 * Erasure failures land in `KeyValueStore.pendingErasures` exactly as before. The pass running at
 * every construction *is* the durable retry: no per-instance bookkeeping has to survive a reload.
 *
 * Accepted bounds, pre-declared: one physical read per governed key at startup (eight keys at
 * Phase 0, plus one confirmation read for each that throws) and one enumeration of the namespace,
 * which no longer reads values at all; an older build erasing a newer build's quarantine
 * diagnostics, which are disposable by design; and a backend whose reads keep failing having its
 * governed keys erased rather than assumed clean (D-032).
 */
export function decontaminateNamespace(
  keyValueStore: KeyValueStore,
  jsonOptions: JsonStoreOptions,
  records: readonly GovernedRecord[] = GOVERNED_RECORDS,
): void {
  const store = createJsonStore(keyValueStore, jsonOptions);
  const governed = new Map(records.map((record) => [record.key, record.project]));

  store.withGovernedScope(() => {
    // Governed keys first, from the table rather than from the backend listing: a key the
    // backend cannot enumerate is still a key we are responsible for.
    for (const record of records) store.decontaminate(record.key, record.project);

    // Then every key *name* the backend is physically holding. Enumerated after the loop above so
    // a diagnostic that loop just wrote is validated too — this build must recognise its own
    // handwriting, and a test that proves it is cheap insurance against the validator drifting
    // away from the writer.
    //
    // Names, not entries: enumeration is value-blind (R7-3). Whether a value means anything is
    // decided below, and only for the names this build chose.
    const enumeration = keyValueStore.physicalKeys();

    for (const key of enumeration.keys) {
      if (governed.has(key)) continue;

      if (key.startsWith(QUARANTINE_PREFIX)) {
        const subject = key.slice(QUARANTINE_PREFIX.length);
        // A diagnostic is only ever filed against a governed key, because those are the only
        // keys anything quarantines. Filed against anything else, the storage key itself is
        // unvouched-for text — and text is where a payload hides.
        if (governed.has(subject)) {
          const physical = keyValueStore.readPhysical(key);
          // Absent, or erasure residue under a name this build composed from a governed key:
          // nothing is stored and nothing is spent.
          if (physical.status === 'absent') continue;
          // 'unreadable' falls through to erasure: an unproven diagnostic is worth nothing (R7-1).
          if (physical.status === 'present' && isCurrentQuarantineText(physical.value, subject)) {
            continue;
          }

          // The name is one this build would have written, so destroying the bytes is enough.
          keyValueStore.erase(key);
          continue;
        }
      }

      // Erased, not removed: whatever a previous build or another actor thought was safe to keep
      // here gets the same physical treatment as any other rejected record — and because nothing
      // vouches for this *name*, only deletion finishes the job. A tombstone would leave
      // `thrive.v1.<attacker text>` sitting in the backend as data in its own right (R7-3), so
      // the key stays in `pendingErasures` until it is really gone. The value is never read: it
      // cannot change the answer.
      keyValueStore.erase(key, { requireRemoval: true });
    }

    /**
     * The namespace could not be walked to the end (S1-4).
     *
     * Fail-closed ownership is property 3 above, and it is a claim about *every* key under
     * `thrive.v1.` — so a walk that could not finish leaves the claim unproven. Unknown names may
     * still be stored, and this build cannot name them to erase them: nothing else can be done
     * about the names themselves. What it can do is stop pretending. The bytes it *can* reach are
     * treated the way unreadable bytes already are (D-032(1)): a backend that cannot be inspected
     * cannot be vouched for, and D-029 settled the direction — losing a legitimate record beats
     * retaining forbidden ones.
     *
     * The blast radius is exactly the accepted read-outage bound: the governed keys are erased and
     * a family starts again, on a backend that is already failing. It is not widened here.
     */
    if (!enumeration.complete) {
      for (const record of records) keyValueStore.erase(record.key);
    }
  });
}
